import { ref, onUnmounted, computed, watch } from 'vue'
import firebase from 'firebase/compat/app'
import { useStore } from 'vuex'

/**
 * Composable for WebSocket connection to chat sessions
 *
 * Provides real-time streaming of chat messages and report content
 * from the backend Anthropic API integration.
 */
export function useChatWebSocket() {
  const store = useStore()
  const ws = ref(null)
  const isConnected = ref(false)
  const isStreaming = ref(false)
  const streamingMessage = ref('')
  const streamingReport = ref('')
  const connectionError = ref(null)
  const reconnectAttempts = ref(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = ref(1000)  // Start with 1 second

  // Event handlers
  const messageHandlers = new Set()
  const reportHandlers = new Set()
  const completeHandlers = new Set()
  const errorHandlers = new Set()

  /**
   * Connect to WebSocket for a specific chat session
   */
  const connect = async (organizationId, sessionCode) => {
    try {
      // Get Firebase token for authentication (will send after connection)
      if (!firebase.auth().currentUser) {
        throw new Error('[WebSocket] User is not authenticated. Cannot connect to chat WebSocket.')
      }
      const firebaseToken = await firebase.auth().currentUser.getIdToken(true)

      // Use the existing /api/3/ws endpoint (reusing infrastructure)
      let wsUrl
      if (import.meta.env.DEV) {
        // Development: connect to backend directly
        wsUrl = `ws://localhost:8000/api/3/ws`
      } else {
        // Production: use same host with wss protocol
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const host = window.location.host
        wsUrl = `${protocol}//${host}/api/3/ws`
      }

      console.log('[WebSocket] Connecting to:', wsUrl)

      // Create WebSocket connection
      ws.value = new WebSocket(wsUrl)

      // Store session info for later subscription
      const chatTopic = `chat:${sessionCode}`

      // Connection opened
      ws.value.onopen = () => {
        console.log('[WebSocket] Connected, sending authentication...')

        // Send authentication token as first message (not in URL for security)
        ws.value.send(JSON.stringify({
          type: 'auth',
          token: firebaseToken
        }))

        // Don't set isConnected yet - wait for auth confirmation
      }

      // Listen for messages
      ws.value.onmessage = (event) => {
        try {
          // Handle non-JSON messages (like plain text ping/pong)
          if (typeof event.data === 'string' && !event.data.startsWith('{')) {
            console.log('[WebSocket] Non-JSON message received:', event.data)
            return
          }

          const data = JSON.parse(event.data)
          console.log('[WebSocket] Message received:', data.type || data.event)

          // Handle authentication confirmation
          if (data.type === 'authenticated') {
            console.log('[WebSocket] Authenticated, subscribing to chat topic...')
            isConnected.value = true
            connectionError.value = null
            reconnectAttempts.value = 0
            reconnectDelay.value = 1000

            // Subscribe to chat topic after authentication
            ws.value.send(JSON.stringify({
              action: 'sub',
              topic: chatTopic
            }))
            return
          }

          // Handle subscription confirmation
          if (data.type === 'subscribed') {
            console.log('[WebSocket] Subscribed to topic:', data.topic)
            return
          }

          // Handle events from ConnectionManager (topic-based)
          if (data.type === 'event') {
            handleEvent(data.event, data.data)
            return
          }

          // Handle direct message types
          handleEvent(data.type, data)
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error)
        }
      }

      // Handle errors
      ws.value.onerror = (error) => {
        console.error('[WebSocket] Error:', error)
        connectionError.value = 'WebSocket connection error'
        errorHandlers.forEach(handler => handler(error))
      }

      // Handle connection close
      ws.value.onclose = (event) => {
        console.log('[WebSocket] Disconnected', event.code, event.reason)
        isConnected.value = false
        isStreaming.value = false

        // Attempt reconnection if not a normal closure
        if (event.code !== 1000 && reconnectAttempts.value < maxReconnectAttempts) {
          reconnectAttempts.value++
          console.log(`[WebSocket] Reconnecting (attempt ${reconnectAttempts.value}/${maxReconnectAttempts})...`)

          setTimeout(() => {
            connect(organizationId, sessionCode)
          }, reconnectDelay.value)

          // Exponential backoff
          reconnectDelay.value = Math.min(reconnectDelay.value * 2, 30000)
        } else if (reconnectAttempts.value >= maxReconnectAttempts) {
          connectionError.value = 'Failed to reconnect after multiple attempts'
        }
      }

    } catch (error) {
      console.error('[WebSocket] Connection error:', error)
      connectionError.value = error.message
      throw error
    }
  }

  /**
   * Handle incoming WebSocket events
   */
  const handleEvent = (event, data) => {
    switch (event) {
      case 'stream_start':
        console.log('[WebSocket] Streaming started')
        isStreaming.value = true
        streamingMessage.value = ''
        streamingReport.value = ''
        break

      case 'message_chunk':
        // Chat message chunk received
        streamingMessage.value += data.chunk
        messageHandlers.forEach(handler => handler(data.chunk, streamingMessage.value))
        break

      case 'report_chunk':
        // Report content chunk received
        streamingReport.value += data.chunk
        reportHandlers.forEach(handler => handler(data.chunk, streamingReport.value))
        break

      case 'stream_complete':
        // Streaming complete with metadata
        console.log('[WebSocket] Streaming complete:', data)
        isStreaming.value = false
        completeHandlers.forEach(handler => handler({
          messageCode: data.message_code,
          versionNumber: data.version_number,
          stats: data.stats,
          message: streamingMessage.value,
          report: streamingReport.value
        }))

        // Reset streaming buffers
        streamingMessage.value = ''
        streamingReport.value = ''
        break

      case 'error':
        console.error('[WebSocket] Stream error:', data)
        isStreaming.value = false
        connectionError.value = data.message || 'Streaming error occurred'
        errorHandlers.forEach(handler => handler(data))
        break

      case 'pong':
        // Keep-alive response
        break

      default:
        console.log('[WebSocket] Unknown event:', event, data)
    }
  }

  /**
   * Disconnect WebSocket
   */
  const disconnect = () => {
    if (ws.value) {
      console.log('[WebSocket] Disconnecting...')
      ws.value.close(1000, 'Client disconnecting')
      ws.value = null
      isConnected.value = false
      isStreaming.value = false
    }
  }

  /**
   * Send a ping to keep connection alive
   */
  const ping = () => {
    if (ws.value && isConnected.value) {
      ws.value.send(JSON.stringify({ type: 'ping' }))
    }
  }

  /**
   * Register event handlers
   */
  const onMessageChunk = (handler) => {
    messageHandlers.add(handler)
    return () => messageHandlers.delete(handler)
  }

  const onReportChunk = (handler) => {
    reportHandlers.add(handler)
    return () => reportHandlers.delete(handler)
  }

  const onStreamComplete = (handler) => {
    completeHandlers.add(handler)
    return () => completeHandlers.delete(handler)
  }

  const onError = (handler) => {
    errorHandlers.add(handler)
    return () => errorHandlers.delete(handler)
  }

  // Auto-disconnect on unmount
  onUnmounted(() => {
    disconnect()
  })

  // Start keep-alive ping every 30 seconds
  let pingInterval = null
  const startPing = () => {
    if (pingInterval) clearInterval(pingInterval)
    pingInterval = setInterval(ping, 30000)
  }

  const stopPing = () => {
    if (pingInterval) {
      clearInterval(pingInterval)
      pingInterval = null
    }
  }

  // Start/stop ping when connection changes
  const connectionStatus = computed(() => isConnected.value)
  let unwatchConnection = null
  if (typeof window !== 'undefined') {
    unwatchConnection = watch(connectionStatus, (connected) => {
      if (connected) {
        startPing()
      } else {
        stopPing()
      }
    })
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopPing()
    if (unwatchConnection) unwatchConnection()
  })

  return {
    // State
    isConnected,
    isStreaming,
    streamingMessage,
    streamingReport,
    connectionError,

    // Methods
    connect,
    disconnect,
    ping,

    // Event handlers
    onMessageChunk,
    onReportChunk,
    onStreamComplete,
    onError
  }
}
